import React from 'react';
import { Code2, Globe, PhoneCall, Plus, Search } from 'lucide-react';

/**
 * Production "Use" patterns: batch calling list + website embed with floating chat widget.
 */
export default function TutorialUseChannelsMockup() {
  const primary = '#459EA3';

  return (
    <div className="mt-5" aria-hidden>
      <p className="text-sm font-semibold text-[#459EA3] uppercase tracking-wide mb-2">Put it to work</p>
      <p className="text-sm text-gray-500 mb-4 max-w-4xl">
        Assign scripts under <strong className="text-gray-700">Account → Phone Numbers</strong>. Use{' '}
        <strong className="text-gray-700">Batch Call</strong> to work through a contact list with your agent. For the web, open{' '}
        <strong className="text-gray-700">Deploy → Chatbot</strong> and use <strong className="text-gray-700">Deploy embedding</strong> to paste an{' '}
        <strong className="text-gray-700">iframe</strong> on your site — visitors get a floating chat widget like the preview here.
      </p>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 sm:gap-6 min-h-[min(52vh,440px)]">
        {/* Batch Call */}
        <div className="relative rounded-2xl border border-gray-200 overflow-hidden shadow-md flex flex-col min-h-[min(48vh,380px)]">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#eef3ff] via-white to-white" />
          <div className="relative p-4 sm:p-5 flex flex-col flex-1 min-h-0">
            <div className="flex items-center gap-3 mb-4 shrink-0">
              <div className="w-10 h-10 rounded-xl bg-white ring-1 ring-black/5 shadow flex items-center justify-center text-gray-900">
                <PhoneCall className="w-5 h-5" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                Batch Call
              </h3>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mb-4 shrink-0">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <div className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg bg-white text-xs text-gray-400">
                  Search contacts…
                </div>
              </div>
              <span className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium shrink-0 pointer-events-none">
                <Plus className="w-4 h-4" />
                Add Contact
              </span>
            </div>
            <div className="flex-1 min-h-0 bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden flex flex-col">
              <div className="overflow-x-auto flex-1 min-h-0">
                <table className="min-w-full divide-y divide-gray-200 text-left text-[11px] sm:text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      {['First name', 'Last name', 'Phone', 'Birthdate', 'Actions'].map((h) => (
                        <th
                          key={h}
                          className="px-3 py-2 sm:px-4 sm:py-2.5 font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200 text-gray-700">
                    {[
                      ['Jordan', 'Lee', '+1 555-0101', '1990-04-12'],
                      ['Sam', 'Rivera', '+1 555-0102', '1985-11-03'],
                    ].map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50/80">
                        {row.map((cell, j) => (
                          <td key={j} className="px-3 py-2 sm:px-4 sm:py-3 whitespace-nowrap">
                            {cell}
                          </td>
                        ))}
                        <td className="px-3 py-2 sm:px-4 sm:py-3">
                          <span className="inline-flex rounded-md bg-blue-600 text-white px-2.5 py-1 text-[10px] sm:text-xs font-semibold">
                            Call
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3 leading-relaxed shrink-0">
              Each row can open a call flow with your saved script and outbound line — ideal for campaigns and follow-ups.
            </p>
          </div>
        </div>

        {/* Website + embed widget */}
        <div className="flex flex-col min-h-[min(48vh,380px)] rounded-2xl border border-gray-200 bg-slate-100 shadow-md overflow-hidden">
          <div className="shrink-0 px-3 py-2 sm:px-4 border-b border-slate-200 bg-slate-200/80 flex items-center gap-2">
            <Globe className="w-4 h-4 text-slate-600 shrink-0" />
            <span className="text-xs sm:text-sm font-semibold text-slate-800">Your website</span>
            <span className="ml-auto text-[10px] text-slate-500 font-mono truncate max-w-[45%]">yoursite.com</span>
          </div>

          <div className="flex-1 min-h-0 p-3 sm:p-4 flex flex-col gap-3">
            <div className="shrink-0 rounded-xl border border-slate-200 bg-slate-900 text-slate-100 p-3 sm:p-3.5">
              <div className="flex items-center gap-2 mb-2 text-slate-400">
                <Code2 className="w-3.5 h-3.5 shrink-0" />
                <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide">Embed snippet</span>
              </div>
              <pre className="text-[9px] sm:text-[10px] font-mono leading-relaxed whitespace-pre-wrap break-all text-emerald-100/95">
                {`<iframe src="…/#/embed?scriptId=YOUR_ID"
  width="420" height="600" frameborder="0"
  title="Chat"></iframe>`}
              </pre>
            </div>

            <div className="flex-1 min-h-[200px] relative rounded-xl bg-white border border-dashed border-slate-300 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-white p-4 text-xs text-slate-400">
                <p className="font-medium text-slate-500 mb-1">Page content</p>
                <p className="leading-relaxed max-w-[60%]">
                  Your marketing copy, pricing, and forms stay here. The chat widget floats above the page — visitors never leave your brand.
                </p>
              </div>

              {/* Floating widget — mirrors ChatbotWidget: bottom-right, dark chrome */}
              <div
                className="absolute bottom-3 right-3 z-[1] flex flex-col rounded-2xl border border-gray-700 bg-black shadow-2xl overflow-hidden w-[min(92%,260px)] sm:w-[240px] max-h-[min(72%,320px)]"
                style={{ boxShadow: '0 25px 50px -12px rgba(0,0,0,0.45)' }}
              >
                <div
                  className="shrink-0 flex items-center justify-between text-white px-3 py-2"
                  style={{
                    background: `linear-gradient(to right, ${primary}, #3d8a8e)`,
                  }}
                >
                  <span className="font-semibold text-[11px] sm:text-xs truncate">Chat with Maya</span>
                  <span className="w-5 h-5 rounded-md bg-white/20 shrink-0" />
                </div>
                <div className="flex-1 min-h-[120px] flex flex-col p-2 bg-[#F9F9F9]">
                  <div className="flex justify-end mb-2">
                    <div
                      className="rounded-lg py-1.5 px-2 max-w-[85%] text-[10px] text-white leading-snug"
                      style={{ background: primary }}
                    >
                      I need a quote
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="rounded-lg py-1.5 px-2 max-w-[85%] text-[10px] bg-white border border-gray-200 text-gray-700 leading-snug">
                      Happy to help — what&apos;s your email?
                    </div>
                  </div>
                  <div className="mt-auto flex gap-1 pt-2 border-t border-gray-200">
                    <div className="flex-1 rounded-lg bg-gray-200 h-7" />
                    <div className="w-7 h-7 rounded-lg shrink-0" style={{ backgroundColor: primary }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p className="shrink-0 text-xs text-slate-600 px-4 pb-3 leading-relaxed border-t border-slate-200/80 bg-slate-50/90 pt-2">
            From Chatbot, open <strong className="text-slate-800">Deploy embedding</strong> to copy a live snippet with your script ID, size, and colors.
          </p>
        </div>
      </div>
    </div>
  );
}
